<template>
  <div class="space-y-6">

    <!-- Grille principale -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Colonne de gauche - Informations personnelles -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Carte Informations personnelles -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
              Informations personnelles
            </h3>
          </div>
          <div class="p-6">
            <form @submit.prevent="updateProfile">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Nom -->
                <div>
                  <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nom complet *
                  </label>
                  <input type="text" id="name" v-model="profileForm.name" required
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    :disabled="updatingProfile">
                </div>

                <!-- Email -->
                <div>
                  <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Adresse email *
                  </label>
                  <input type="email" id="email" v-model="profileForm.email" required
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    :disabled="updatingProfile">
                </div>

                <!-- Téléphone -->
                <div>
                  <label for="telephone" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Téléphone
                  </label>
                  <input type="tel" id="telephone" v-model="profileForm.telephone"
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    :disabled="updatingProfile">
                </div>

                <!-- Wilaya -->
                <div>
                  <label for="wilaya" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Wilaya
                  </label>
                  <input type="text" id="wilaya" v-model="profileForm.wilaya"
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    :disabled="updatingProfile">
                </div>
              </div>

              <!-- Bouton de sauvegarde -->
              <div class="flex justify-end mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button type="submit" :disabled="updatingProfile || !hasProfileChanges" :class="[
                  'px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center space-x-2',
                  hasProfileChanges && !updatingProfile
                    ? 'bg-blue-900 text-white hover:bg-blue-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]">
                  <svg v-if="updatingProfile" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                    fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                    </path>
                  </svg>
                  <span>{{ updatingProfile ? 'Mise à jour...' : 'Enregistrer les modifications' }}</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Carte Modification du mot de passe -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z">
                </path>
              </svg>
              Modification du mot de passe
            </h3>
          </div>
          <div class="p-6">
            <form @submit.prevent="updatePassword">
              <div class="space-y-4">
                <!-- Mot de passe actuel -->
                <div>
                  <label for="current_password" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Mot de passe actuel *
                  </label>
                  <input type="password" id="current_password" v-model="passwordForm.current_password" required
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    :disabled="updatingPassword">
                </div>

                <!-- Nouveau mot de passe -->
                <div>
                  <label for="new_password" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nouveau mot de passe *
                  </label>
                  <input type="password" id="new_password" v-model="passwordForm.new_password" required
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    :disabled="updatingPassword">
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Le mot de passe doit contenir au moins 8 caractères
                  </p>
                </div>

                <!-- Confirmation du mot de passe -->
                <div>
                  <label for="new_password_confirmation"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirmer le nouveau mot de passe *
                  </label>
                  <input type="password" id="new_password_confirmation" v-model="passwordForm.new_password_confirmation"
                    required
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    :disabled="updatingPassword">
                </div>
              </div>

              <!-- Bouton de sauvegarde -->
              <div class="flex justify-end mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button type="submit" :disabled="updatingPassword || !hasPasswordChanges" :class="[
                  'px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center space-x-2',
                  hasPasswordChanges && !updatingPassword
                    ? 'bg-blue-900 text-white hover:bg-blue-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]">
                  <svg v-if="updatingPassword" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                    fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                    </path>
                  </svg>
                  <span>{{ updatingPassword ? 'Mise à jour...' : 'Changer le mot de passe' }}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Colonne de droite - Informations du compte -->
      <div class="space-y-6">
        <!-- Carte Résumé du compte -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
              Résumé du compte
            </h3>
          </div>
          <div class="p-6">
            <!-- Avatar et nom -->
            <div class="text-center mb-6">
              <div
                class="h-20 w-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold text-2xl mx-auto mb-3 shadow-lg">
                {{ userInitials }}
              </div>
              <h4 class="text-xl font-bold text-gray-900 dark:text-gray-100">{{ user.name }}</h4>
              <p class="text-gray-600 dark:text-gray-400">{{ user.email }}</p>
            </div>

            <!-- Informations du compte -->
            <div class="space-y-4">
              <div class="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Rôle</span>
                <span class="text-sm text-gray-900 dark:text-gray-100">{{ userRole }}</span>
              </div>
              <div v-if="user.telephone"
                class="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Téléphone</span>
                <span class="text-sm text-gray-900 dark:text-gray-100">{{ user.telephone }}</span>
              </div>
              <div v-if="user.wilaya"
                class="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Wilaya</span>
                <span class="text-sm text-gray-900 dark:text-gray-100">{{ user.wilaya }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Carte Domaines d'expertise (pour les responsables) -->
        <div v-if="hasResponsableRole"
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10">
                </path>
              </svg>
              Gamme & motif
            </h3>
          </div>
          <div class="p-6">
            <!-- Gammes -->
            <div class="mb-4">
              <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Gammes assignées</h4>
              <div class="space-y-2">
                <div v-for="gamme in userGammes" :key="gamme.id"
                  class="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                  {{ gamme.name }}
                </div>
                <div v-if="userGammes.length === 0" class="text-xs text-gray-500 dark:text-gray-400">
                  Aucune gamme assignée
                </div>
              </div>
            </div>

            <!-- Motifs -->
            <div>
              <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Motifs assignés</h4>
              <div class="space-y-2">
                <div v-for="motif in userMotifs" :key="motif.id"
                  class="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                  {{ motif.name }}
                </div>
                <div v-if="userMotifs.length === 0" class="text-xs text-gray-500 dark:text-gray-400">
                  Aucun motif assigné
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProfileContent',
  props: {
    user: {
      type: Object,
      required: true,
      default: () => ({
        name: '',
        email: '',
        telephone: '',
        wilaya: '',
        roles: [],
        created_at: null,
        last_login_at: null
      })
    },
    userRoles: {
      type: Array,
      default: () => []
    },
    userPermissions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      updatingProfile: false,
      updatingPassword: false,
      profileForm: {
        name: '',
        email: '',
        telephone: '',
        wilaya: ''
      },
      passwordForm: {
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
      },
      originalProfile: {}
    };
  },
  computed: {
    userInitials() {
      if (!this.user?.name) return '??';
      return this.user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },

    userRole() {
      const roles = this.userRolesArray;
      if (roles.length > 0) {
        return roles[0].name || roles[0];
      }
      return 'Utilisateur';
    },

    userRolesArray() {
      if (this.user?.roles && this.user.roles.length > 0) {
        return this.user.roles;
      }
      return this.userRoles || [];
    },

    safeUserPermissions() {
      return this.userPermissions || [];
    },

    hasResponsableRole() {
      const roles = this.userRolesArray;
      return roles.some(role => {
        const roleName = (role.name || role).toLowerCase();
        return roleName.includes('responsable');
      });
    },

    userGammes() {
      return this.user.gammes_responsable ||
        this.user.responsable_gammes ||
        this.user.gammesResponsable ||
        this.user.gammes ||
        [];
    },

    userMotifs() {
      return this.user.motifs_responsable ||
        this.user.responsable_motifs ||
        this.user.motifsResponsable ||
        this.user.motifs ||
        [];
    },

    hasProfileChanges() {
      return JSON.stringify(this.profileForm) !== JSON.stringify(this.originalProfile);
    },

    hasPasswordChanges() {
      return this.passwordForm.current_password &&
        this.passwordForm.new_password &&
        this.passwordForm.new_password_confirmation;
    }
  },
  watch: {
    user: {
      handler(newUser) {
        this.initializeForm();
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    initializeForm() {
  this.profileForm = {
    name: this.user?.name || '',
    email: this.user?.email || '',
    telephone: this.user?.telephone || '',
    wilaya: this.user?.wilaya || '' // Vérifiez que cette ligne fonctionne
  };
  this.originalProfile = { ...this.profileForm };
},

    async updateProfile() {
      this.updatingProfile = true;
      try {

        const token = this.getCsrfToken();
        const response = await fetch('/api/profile', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': token,
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(this.profileForm)
        });

        const data = await response.json();
        console.log('Réponse API:', data); // Debug

        if (response.ok) {
          this.showSuccess('Profil mis à jour avec succès');
          this.originalProfile = { ...this.profileForm };

          // Émettre l'événement avec les données mises à jour
          if (data.user) {
            this.$emit('profile-updated', data.user);
          }
        } else {
          this.showError(data.message || 'Erreur lors de la mise à jour du profil');
        }
      } catch (error) {
        console.error('Erreur détaillée:', error);
        this.showError('Erreur de connexion lors de la mise à jour du profil');
      } finally {
        this.updatingProfile = false;
      }
    },

    async updatePassword() {
      if (this.passwordForm.new_password !== this.passwordForm.new_password_confirmation) {
        this.showError('Les mots de passe ne correspondent pas');
        return;
      }

      if (this.passwordForm.new_password.length < 8) {
        this.showError('Le mot de passe doit contenir au moins 8 caractères');
        return;
      }

      this.updatingPassword = true;
      try {
        const token = this.getCsrfToken();
        const response = await fetch('/api/profile/password', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': token,
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            current_password: this.passwordForm.current_password,
            new_password: this.passwordForm.new_password,
            new_password_confirmation: this.passwordForm.new_password_confirmation
          })
        });

        const data = await response.json();

        if (response.ok) {
          this.showSuccess('Mot de passe modifié avec succès');
          this.passwordForm = {
            current_password: '',
            new_password: '',
            new_password_confirmation: ''
          };
        } else {
          this.showError(data.message || 'Erreur lors de la modification du mot de passe');
        }
      } catch (error) {
        this.showError('Erreur de connexion lors de la modification du mot de passe');
      } finally {
        this.updatingPassword = false;
      }
    },

    getCsrfToken() {
      const metaTag = document.querySelector('meta[name="csrf-token"]');
      if (metaTag) {
        return metaTag.getAttribute('content');
      }
      return '';
    },

    showSuccess(message) {
      // Remplacer par votre système de notification préféré
      alert('Succès: ' + message);
    },

    showError(message) {
      // Remplacer par votre système de notification préféré
      alert('Erreur: ' + message);
    }
  }
};
</script>